/**
 * O que faz: normaliza lançamentos e calcula métricas rastreáveis de vendas.
 * Onde usa: painel de vendas e modal de faturamento do dashboard.
 * Conexões: faturamentoService, cálculo financeiro existente e rotas do chat.
 * Tabelas: nenhuma consulta; recebe vendas, pagamentos e leads já autorizados.
 * Data: 30/08/2026.
 */
import type { DadosFaturamento, LeadFaturamento } from '@/services/faturamentoService';
import { montarFaturamentoDashboard } from './montarFaturamentoDashboard';

export type OrigemFaturamento = 'venda' | 'consulta';
export interface LancamentoFaturamento {
  chave: string;
  id: string;
  tipo: OrigemFaturamento;
  descricao: string;
  valor: number;
  data: string;
  criadoEm: string;
  origem: string;
  formaPagamento: string | null;
  parcelas: number | null;
  dataLegada: boolean;
  lead: LeadFaturamento | null;
}

export function montarPainelVendas(dados: DadosFaturamento, clinicaId: string) {
  // Mesmo administradores só veem o lead da clínica analisada, sem misturar vínculos inconsistentes.
  const leadDaClinica = (lead?: LeadFaturamento | null) => lead?.clinica_id === clinicaId ? lead : null;
  const lancamentos: LancamentoFaturamento[] = [
    ...dados.vendas.map(venda => ({
      chave: `venda:${venda.id}`, id: venda.id, tipo: 'venda' as const,
      descricao: venda.nome_servico, valor: Number(venda.valor) || 0,
      data: venda.data_venda, criadoEm: venda.created_at, origem: venda.origem,
      formaPagamento: null, parcelas: null, dataLegada: false, lead: leadDaClinica(venda.lead),
    })),
    ...dados.pagamentos.map(pagamento => ({
      chave: `consulta:${pagamento.id}`, id: pagamento.id, tipo: 'consulta' as const,
      descricao: pagamento.titulo || 'Consulta', valor: Number(pagamento.valor) || 0,
      data: pagamento.pago_em ?? pagamento.data_inicio, criadoEm: pagamento.created_at, origem: 'agenda',
      formaPagamento: pagamento.forma_pagamento, parcelas: pagamento.parcelas,
      dataLegada: !pagamento.pago_em, lead: leadDaClinica(pagamento.lead),
    })),
  ].sort((a, b) => new Date(b.data).getTime() - new Date(a.data).getTime() || a.chave.localeCompare(b.chave));
  const totais = montarFaturamentoDashboard(dados.pagamentos, dados.vendas);
  // Uma pessoa pode comprar várias vezes: conta IDs distintos, nunca o número de vendas como leads.
  const leadsCompradores = new Set(dados.vendas.map(venda => venda.lead_id)).size;
  return {
    lancamentos, ...totais, quantidadeVendas: dados.vendas.length,
    quantidadeConsultas: dados.pagamentos.length, leadsCompradores,
    ticketMedio: dados.vendas.length ? totais.faturamentoVendas / dados.vendas.length : 0,
  };
}

export type PainelVendas = ReturnType<typeof montarPainelVendas>;
export const formatarMoedaVenda = (valor: number) => valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
export const formatarDataVenda = (data: string) => new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo' }).format(new Date(data));

export function rotaChatVenda(leadId: string, clinicaAdmin?: string) {
  const parametros = new URLSearchParams({ leadId });
  if (clinicaAdmin) parametros.set('adminClinicId', clinicaAdmin);
  return `/chat?${parametros.toString()}`;
}

export function parametrosPeriodoVendas(inicio: Date | null, fim: Date | null, rotulo: string, clinicaAdmin?: string) {
  const parametros = new URLSearchParams({ periodo: rotulo });
  // Preserva os instantes exatos do card, inclusive o filtro Máximo sem limites.
  if (inicio) parametros.set('inicio', inicio.toISOString());
  if (fim) parametros.set('fim', fim.toISOString());
  if (clinicaAdmin) parametros.set('adminClinicId', clinicaAdmin);
  return parametros;
}
